/**
 * Google Drive Frontend Service
 * 
 * Handles Drive connection status and file uploads from the frontend
 */

import { supabase } from './supabaseClient';

const isLocalhost = (): boolean => {
    const hostname = window.location?.hostname || '';
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname === '0.0.0.0';
    console.log('[Drive] isLocalhost check:', hostname, isLocal);
    return isLocal;
};

/**
 * Check if Google Drive integration is enabled on the server
 */
export const isDriveEnabled = async (): Promise<boolean> => {
    try {
        const res = await fetch('/api/google/drive/enabled');
        const data = await res.json();
        return data.enabled === true;
    } catch {
        return false;
    }
};

/**
 * Check if current user has connected their Drive
 */
export const isDriveConnected = async (): Promise<boolean> => {
    if (isLocalhost()) return true; // Bypass for development
    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;

        if (!token) return false;

        const res = await fetch('/api/google/drive/status', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        const data = await res.json();
        return data.connected === true;
    } catch {
        return false;
    }
};

/**
 * Start OAuth flow to connect Drive
 * Opens in current window (will redirect back after auth)
 */
export const connectDrive = async (): Promise<void> => {
    if (isLocalhost()) {
        console.log('[Drive] Dev Mode: Simulating connection...');
        alert('Dev Mode: Drive connected "successfully" (bypassed).');
        return;
    }

    console.log('[Drive] Starting OAuth flow...');

    const session = await supabase?.auth.getSession();
    const userId = session?.data.session?.user?.id;
    const jwt = session?.data.session?.access_token;

    if (!userId || !jwt) {
        console.error('[Drive] No session found');
        throw new Error('Vous devez être connecté pour lier Google Drive');
    }

    console.log('[Drive] User authenticated:', userId.substring(0, 8) + '...');

    // Fetch auth URL from backend (requires JWT for RLS-first flow)
    let response: Response;
    try {
        const origin = window.location.origin;
        response = await fetch(`/api/google/drive/auth?origin=${encodeURIComponent(origin)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${jwt}`
            }
        });
    } catch (networkError) {
        console.error('[Drive] Network error:', networkError);
        throw new Error('Erreur réseau. Vérifiez votre connexion internet.');
    }

    // Parse response safely (handle non-JSON responses)
    const responseText = await response.text();
    let data: { code?: string; message?: string; details?: string; hint?: string; authUrl?: string; requestId?: string };
    try {
        data = JSON.parse(responseText);
    } catch (e) {
        console.error('[Drive] Server returned invalid JSON:', responseText.substring(0, 500));
        throw new Error(`Erreur serveur (status ${response.status}). Consultez les logs Vercel.`);
    }

    if (!response.ok) {
        console.error('[Drive] Auth initiation failed:', data);

        // Provide user-friendly error messages
        const errorCode = data.code || 'UNKNOWN';
        const errorMessage = data.message || 'Erreur inconnue';
        const details = data.details || '';
        const hint = data.hint || '';

        let userMessage = '';
        switch (errorCode) {
            case 'DRIVE_NOT_CONFIGURED':
                userMessage = 'Google Drive n\'est pas configuré sur le serveur. Contactez l\'administrateur.';
                break;
            case 'AUTH_REQUIRED':
                userMessage = 'Session expirée. Veuillez vous reconnecter.';
                break;
            case 'STATE_CREATE_FAILED':
                userMessage = `Erreur base de données: ${details}. ${hint}`;
                break;
            default:
                userMessage = `${errorMessage}${details ? ` (${details})` : ''}`;
        }

        throw new Error(userMessage);
    }

    const { authUrl, requestId } = data;
    console.log('[Drive] Auth URL received, requestId:', requestId);

    if (!authUrl) {
        console.error('[Drive] No authUrl in response:', data);
        throw new Error('Le serveur n\'a pas retourné d\'URL d\'authentification');
    }

    console.log('[Drive] Redirecting to Google OAuth...');
    // Redirect to Google OAuth
    window.location.href = authUrl;
};

/**
 * Upload a file to user's Google Drive
 */
export const uploadToDrive = async (
    fileUrl: string,
    fileName: string,
    mimeType: string = 'video/mp4',
    folderId?: string
): Promise<{ success: boolean; fileId?: string; webViewLink?: string; webContentLink?: string; error?: string }> => {
    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;

        if (!token) {
            return { success: false, error: 'NOT_AUTHENTICATED' };
        }

        const res = await fetch('/api/google/drive/upload-from-url', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ fileUrl, fileName, mimeType, folderId })
        });

        const data = await res.json();

        if (!res.ok) {
            return { success: false, error: data.error || 'UPLOAD_FAILED' };
        }

        return {
            success: true,
            fileId: data.fileId,
            webViewLink: data.webViewLink,
            webContentLink: data.webContentLink
        };
    } catch (error) {
        return { success: false, error: 'NETWORK_ERROR' };
    }
};

/**
 * Upload a Blob directly to user's Google Drive
 * Use this when you have a local blob (not a URL accessible by server)
 */
export const uploadBlobToDrive = async (
    blob: Blob,
    fileName: string,
    mimeType: string = 'video/mp4',
    folderId?: string
): Promise<{ success: boolean; fileId?: string; webViewLink?: string; webContentLink?: string; error?: string }> => {
    if (isLocalhost()) {
        console.log('[Drive] Dev mode - simulating blob upload');
        return {
            success: true,
            fileId: `dev_blob_${Date.now()}`,
            webViewLink: 'https://drive.google.com/file/d/dev_mock/view'
        };
    }

    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;

        if (!token) {
            return { success: false, error: 'NOT_AUTHENTICATED' };
        }

        // Convert blob to base64
        const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        console.log('[Drive] Uploading blob:', fileName, 'Size:', blob.size, 'bytes');

        const res = await fetch('/api/google/drive/upload-blob', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ base64, fileName, mimeType, folderId })
        });

        const data = await res.json();

        if (!res.ok) {
            console.error('[Drive] Blob upload failed:', data);
            return { success: false, error: data.error || 'UPLOAD_FAILED' };
        }

        console.log('[Drive] Blob upload success:', data.fileId);
        return {
            success: true,
            fileId: data.fileId,
            webViewLink: data.webViewLink,
            webContentLink: data.webContentLink
        };
    } catch (error) {
        console.error('[Drive] Blob upload error:', error);
        return { success: false, error: 'NETWORK_ERROR' };
    }
};

/**
 * Update an existing file on User's Google Drive
 */
export const updateDriveFile = async (
    fileId: string,
    fileUrl: string,
    mimeType: string = 'application/json'
): Promise<{ success: boolean; fileId?: string; webViewLink?: string; webContentLink?: string; error?: string }> => {
    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;

        if (!token) {
            return { success: false, error: 'NOT_AUTHENTICATED' };
        }

        const res = await fetch('/api/google/drive/files/update', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ fileId, fileUrl, mimeType })
        });

        const data = await res.json();

        if (!res.ok) {
            return { success: false, error: data.error || 'UPDATE_FAILED' };
        }

        return {
            success: true,
            fileId: data.fileId,
            webViewLink: data.webViewLink,
            webContentLink: data.webContentLink
        };
    } catch (error) {
        return { success: false, error: 'NETWORK_ERROR' };
    }
};

/**
 * List folders from Drive API
 */
export const listDriveFolders = async (parentId?: string): Promise<{ id: string; name: string }[]> => {
    if (isLocalhost()) {
        return [
            { id: 'dev_folder_1', name: 'Dev Folder A' },
            { id: 'dev_folder_2', name: 'Dev Folder B' },
        ];
    }
    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;
        if (!token) return [];

        const url = parentId
            ? `/api/google/drive/folders?parentId=${parentId}`
            : '/api/google/drive/folders';

        const res = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        return data.folders || [];
    } catch {
        return [];
    }
};

/**
 * Create a new folder on Drive
 */
export const createDriveFolder = async (name: string, parentId?: string): Promise<{ id: string; name: string } | null> => {
    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;
        if (!token) return null;

        const res = await fetch('/api/google/drive/folders', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ name, parentId })
        });
        const data = await res.json();
        return data.folder || null;
    } catch {
        return null;
    }
};

/**
 * List files from Drive API (frontend wrapper)
 */
export const listDriveFiles = async (mimeType: string = 'video/*', parentId?: string): Promise<{ id: string; name: string; mimeType: string; thumbnailLink?: string; webViewLink?: string }[]> => {
    if (isLocalhost()) {
        return [
            { id: 'mock_vid_1', name: '[DEV] Test Video 1.mp4', mimeType: 'video/mp4', thumbnailLink: 'https://via.placeholder.com/320x180' },
            { id: 'mock_vid_2', name: '[DEV] Test Video 2.mp4', mimeType: 'video/mp4', thumbnailLink: 'https://via.placeholder.com/320x180' },
            { id: 'mock_img_1', name: '[DEV] Image 1.png', mimeType: 'image/png', thumbnailLink: 'https://via.placeholder.com/150' },
        ].filter(f => mimeType === 'video/*' ? f.mimeType.startsWith('video') : true);
    }

    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;
        if (!token) return [];

        let url = `/api/google/drive/files?mimeType=${encodeURIComponent(mimeType)}`;
        if (parentId) {
            url += `&parentId=${parentId}`;
        }

        const res = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        return data.files || [];
    } catch {
        return [];
    }
};

/**
 * Offload asset from Supabase to Drive (BYOS cold storage)
 * Moves asset from Supabase (transit buffer) to Drive (permanent storage)
 * Optionally deletes from Supabase after successful upload
 */
export interface OffloadResult {
    success: boolean;
    driveFileId?: string;
    webViewLink?: string;
    webContentLink?: string;
    deletedFromSupabase?: boolean;
    error?: string;
}

export const offloadAssetToDrive = async (
    supabaseUrl: string,
    fileName: string,
    mimeType: string = 'video/mp4',
    folderId?: string,
    deleteAfterUpload: boolean = true
): Promise<OffloadResult> => {
    const traceId = `offload-client-${Date.now()}`;
    console.log('[DRIVE][OFFLOAD_CLIENT_START]', { traceId, fileName, folderId, deleteAfterUpload });

    if (isLocalhost()) {
        console.log('[DRIVE][OFFLOAD_DEV_MODE]', { traceId });
        return {
            success: true,
            driveFileId: `dev_drive_${Date.now()}`,
            webViewLink: `https://drive.google.com/file/d/dev_mock/view`,
            deletedFromSupabase: deleteAfterUpload
        };
    }

    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;

        if (!token) {
            return { success: false, error: 'NOT_AUTHENTICATED' };
        }

        const res = await fetch('/api/google/drive/offload-asset', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ supabaseUrl, fileName, mimeType, folderId, deleteAfterUpload })
        });

        const data = await res.json();

        if (!res.ok) {
            console.error('[DRIVE][OFFLOAD_CLIENT_ERROR]', { traceId, error: data.error });
            return { success: false, error: data.error || 'OFFLOAD_FAILED' };
        }

        console.log('[DRIVE][OFFLOAD_CLIENT_SUCCESS]', { traceId, driveFileId: data.driveFileId });
        return {
            success: true,
            driveFileId: data.driveFileId,
            webViewLink: data.webViewLink,
            webContentLink: data.webContentLink,
            deletedFromSupabase: data.deletedFromSupabase
        };
    } catch (error) {
        console.error('[DRIVE][OFFLOAD_CLIENT_NETWORK_ERROR]', { traceId, error });
        return { success: false, error: 'NETWORK_ERROR' };
    }
};

/**
 * Get file content from Drive (for JSON/text files)
 */
export const getDriveFileContent = async (fileId: string): Promise<string | null> => {
    if (isLocalhost()) {
        // Return mock project state for dev
        return JSON.stringify({
            version: '2.0',
            project: { id: 'dev-project', name: 'Dev Project' },
            promptVersions: [],
            currentPromptVersion: 0,
            chatLog: [],
            assets: [],
            jobs: [],
            storyboardPreviews: {},
            savedAt: new Date().toISOString()
        });
    }

    try {
        const session = await supabase?.auth.getSession();
        const token = session?.data.session?.access_token;
        if (!token) return null;

        const res = await fetch(`/api/google/drive/files/${fileId}/content`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!res.ok) {
            console.error('[DRIVE] Failed to get file content:', res.status);
            return null;
        }

        return await res.text();
    } catch (error) {
        console.error('[DRIVE] Error getting file content:', error);
        return null;
    }
};
